import { useCallback, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useRefetchOnResume } from '@/hooks/useRefetchOnResume';
import type { FanQuiz } from '@/lib/quiz';

const QUIZ_LIST_POLL_MS = 30_000;
/** Runs older than this are off the home list anyway (fanQuizzes keeps 20 min after the end). */
const LOOKBACK_MS = 3 * 60 * 60_000;
const COLUMNS = 'id, club_id, event_id, title, prize_text, intro_text, slot, status, started_at, intro_seconds, answer_seconds, reveal_seconds, question_count';

/**
 * The club's started quizzes, read directly (one small indexed select, RLS
 * hides drafts). The realtime row flip is what makes the quiz pop up at once;
 * this is the net under it: a phone whose socket died while locked, or that
 * missed the flip, still sees the quiz on resume and within 30 s. 2000 phones
 * → ~70 tiny reads per second, nothing like the fan-page RPC.
 */
export function useQuizListSync(
  clubId: string | null | undefined,
  onRows: (rows: FanQuiz[]) => void,
  { enabled = true, intervalMs = QUIZ_LIST_POLL_MS }: { enabled?: boolean; intervalMs?: number } = {},
) {
  const onRowsRef = useRef(onRows);
  onRowsRef.current = onRows;
  const read = useCallback(async () => {
    if (!clubId) return;
    // quizzes is newer than the generated types.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await (supabase as any)
      .from('quizzes')
      .select(COLUMNS)
      .eq('club_id', clubId)
      .neq('status', 'draft')
      .gte('started_at', new Date(Date.now() - LOOKBACK_MS).toISOString());
    if (error || !data) return;
    onRowsRef.current(data as FanQuiz[]);
  }, [clubId]);

  const active = enabled && !!clubId;
  useEffect(() => { if (active) void read(); }, [active, read]);
  useRefetchOnResume(read, { enabled: active, intervalMs });
}
